import {get} from "../api/api";

interface SttSession {
    clientId: string;
    sessionId: string;
}

interface TranscriptMessage {
    type: string;
    utterance?: {
        id: string;
        sentence: string;
        words: Array<{ word: string; start: number; end: number }>;
    };
    completed?: boolean;
}

export class WS {
    private webSocket: WebSocket | null = null;
    private sessionId = "";
    private screenStream: MediaStream | null = null;
    private mediaStream: MediaStream | null = null;
    private mediaRecorder: MediaRecorder | null = null;
    private transcriptHandler: (transcript: string, completed: boolean) => void = () => {
    };
    private readonly language: string;

    constructor(language: string) {
        this.language = language;
    }

    async connect() {
        try {
            const session = await get<SttSession>("/stt-session");
            this.sessionId = session.sessionId;

            const model = "juvoly_v3";
            const socketUrl = `wss://services.juvoly.nl/api/v2/socket/${model}?languages=${this.language}`;

            console.info(`Connecting to WebSocket(${this.sessionId}):`, socketUrl);

            this.webSocket = new WebSocket(socketUrl, ["v2.juvoly", session.clientId, session.sessionId]);

            await new Promise<void>((resolve, reject) => {
                this.webSocket!.onerror = (error) => {
                    reject(error);
                };

                this.webSocket!.onmessage = (event) => {
                    const data: TranscriptMessage = JSON.parse(event.data);
                    console.info(`WebSocket(${this.sessionId}) message:`, data);
                    if (data.type === "ready") {
                        console.info(`WebSocket(${this.sessionId}) is ready`);
                        resolve();
                    } else if (data.type === "transcript" && data.utterance) {
                        this.transcriptHandler(data.utterance.sentence, data.completed ?? false);
                    }
                };

                this.webSocket!.onopen = () => {
                    console.info(`WebSocket(${this.sessionId}) connection opened`);
                };
            });
        } catch (error) {
            console.warn("WebSocket connection failed:", error);
            throw error;
        }
    }

    async setAudioScreenInput(getStream: () => Promise<MediaStream>) {
        this.screenStream = await getStream();
        const audioContext = new AudioContext();
        const destination = audioContext.createMediaStreamDestination();

        const screenSource = audioContext.createMediaStreamSource(this.screenStream);
        screenSource.connect(destination);

        await this.setAudioInput(async () => destination.stream);
    }

    async setAudioInput(getStream: () => Promise<MediaStream>) {
        try {
            this.mediaStream = await getStream();
            this.mediaRecorder = new MediaRecorder(this.mediaStream);

            this.mediaRecorder.ondataavailable = async (event) => {
                try {
                    if (this.webSocket?.readyState === WebSocket.OPEN) {
                        const buffer = await event.data.arrayBuffer();
                        this.webSocket?.send(buffer);
                    }
                } catch (err) {
                    console.error(err)
                }
            };

            this.mediaRecorder.start(250);
        } catch (error) {
            console.error("Failed to set audio input:", error);
            throw error;
        }
    }

    setTranscriptOutput(handler: (transcript: string, completed: boolean) => void) {
        this.transcriptHandler = handler;
    }

    stop() {
        this.mediaRecorder?.stop();
        this.mediaStream?.getTracks().forEach(track => track.stop());
        this.screenStream?.getTracks().forEach(track => track.stop());
        this.webSocket?.close();

        this.mediaRecorder = null;
        this.mediaStream = null;
        this.screenStream = null;
        this.webSocket = null;

        console.info(`WebSocket(${this.sessionId}) stopped`);
    }
}
